import { useState } from 'react'
import { XSquare, ArrowRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useCloseCorrection } from '../../hooks/backend/shifts'
import type { PendingCorrection } from './reconcile'

// Close is for corrections that can never be applied — most importantly those
// that would clash with an existing shift on the destination. A reason is
// required so the audit trail records why it was taken out of the queue. On
// success the caller refreshes the pending list.
export function CloseCorrectionDialog({
  row,
  onClosed,
}: {
  row: PendingCorrection
  onClosed: () => void
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { trigger, loading } = useCloseCorrection()

  const trimmed = reason.trim()

  async function handleClose() {
    setError(null)
    try {
      await trigger(
        { correction_id: row.id, reason: trimmed.length > 0 ? trimmed : null },
        { skipCache: true },
      ).result
      toast.success('Correction closed.')
      setOpen(false)
      setReason('')
      onClosed()
    } catch (err) {
      // Surface the DB function's refusal message verbatim.
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setError(null)
        setOpen(next)
      }}
    >
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5 px-2 text-xs"
        onClick={() => {
          setError(null)
          setOpen(true)
        }}
      >
        <XSquare className="h-3.5 w-3.5" />
        Close
      </Button>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Close this correction</DialogTitle>
          <DialogDescription>
            The proposal is taken out of the pending list. Nothing about the shift changes. Use
            this for corrections that can never be applied.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">{row.field || 'value'}</span>
              <span className="font-mono font-semibold text-foreground">
                {row.from_value || '—'}
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-mono font-semibold text-foreground">
                {row.to_value || '—'}
              </span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="close-reason" className="text-xs font-medium text-muted-foreground">
              Reason
            </Label>
            <Textarea
              id="close-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being closed? e.g. approving it would clash on the destination."
              rows={3}
              className="text-sm"
            />
          </div>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading || trimmed.length === 0}
          >
            {loading && <Spinner className="mr-1.5 h-4 w-4" />}
            <XSquare className="mr-1.5 h-4 w-4" />
            Close correction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
